import React from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pencil, Trash2, Check, X } from "lucide-react";
import { formatUSD, formatDate } from "@/lib/format";

const editInputCls =
  "rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900 h-8 text-xs";

function HeaderCell({ children, align = "left" }) {
  return (
    <th className={`font-sans font-medium uppercase tracking-[0.15em] text-[10px] px-3 py-2 text-${align}`}>
      {children}
    </th>
  );
}

function EditRow({ id, draft, setDraft, categories, onSave, onCancel }) {
  return (
    <tr className="border-t border-neutral-100 bg-neutral-50" data-testid={`expense-edit-row-${id}`}>
      <td className="px-3 py-1">
        <Input
          type="date"
          value={draft.expense_date}
          onChange={(ev) => setDraft({ ...draft, expense_date: ev.target.value })}
          className={editInputCls}
        />
      </td>
      <td className="px-3 py-1">
        <Select value={draft.category} onValueChange={(v) => setDraft({ ...draft, category: v })}>
          <SelectTrigger className="rounded-none border-neutral-300 focus:ring-0 h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-none">
            {categories.map((c) => (
              <SelectItem key={c} value={c} className="rounded-none">{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="px-3 py-1">
        <Input
          value={draft.vendor}
          onChange={(ev) => setDraft({ ...draft, vendor: ev.target.value })}
          className={editInputCls}
        />
      </td>
      <td className="px-3 py-1">
        <Input
          value={draft.notes}
          onChange={(ev) => setDraft({ ...draft, notes: ev.target.value })}
          className={editInputCls}
        />
      </td>
      <td className="px-3 py-1">
        {draft.category === "Mileage" ? (
          <Input
            type="number"
            step="0.1"
            placeholder="miles"
            value={draft.miles}
            onChange={(ev) => setDraft({ ...draft, miles: ev.target.value })}
            className={`${editInputCls} text-right font-mono`}
          />
        ) : (
          <Input
            type="number"
            step="0.01"
            value={draft.amount}
            onChange={(ev) => setDraft({ ...draft, amount: ev.target.value })}
            className={`${editInputCls} text-right font-mono`}
          />
        )}
      </td>
      <td className="px-2 py-1">
        <div className="flex gap-1 justify-end">
          <button onClick={onSave} className="text-emerald-700 hover:text-emerald-900" data-testid={`expense-save-${id}`}>
            <Check className="h-4 w-4" strokeWidth={1.5} />
          </button>
          <button onClick={onCancel} className="text-neutral-500 hover:text-neutral-900" data-testid={`expense-cancel-${id}`}>
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>
      </td>
    </tr>
  );
}

function ReadRow({ expense, onEdit, onDelete }) {
  return (
    <tr className="row-hover border-t border-neutral-100" data-testid={`expense-row-${expense.id}`}>
      <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{formatDate(expense.expense_date)}</td>
      <td className="px-3 py-2">
        {expense.category}
        {expense.category === "Mileage" && expense.miles ? (
          <span className="font-mono text-[10px] text-neutral-500 ml-2">
            {expense.miles} mi @ ${expense.mileage_rate?.toFixed(2)}
          </span>
        ) : null}
      </td>
      <td className="px-3 py-2 text-neutral-600">{expense.vendor || "—"}</td>
      <td className="px-3 py-2 text-neutral-600">
        {expense.deal_name && (
          <span className="inline-block mr-2 px-1.5 py-0.5 border border-neutral-300 bg-neutral-50 font-mono text-[10px] text-neutral-700" data-testid={`expense-deal-badge-${expense.id}`}>
            {expense.deal_name}
          </span>
        )}
        {expense.notes || (expense.deal_name ? "" : "—")}
      </td>
      <td className="px-3 py-2 font-mono text-right" style={{ color: "var(--expense)" }}>
        {formatUSD(expense.amount)}
      </td>
      <td className="px-2 py-2">
        <div className="flex gap-2 justify-end">
          <button onClick={() => onEdit(expense)} className="text-neutral-500 hover:text-neutral-900" data-testid={`expense-edit-${expense.id}`}>
            <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
          </button>
          <button onClick={() => onDelete(expense.id)} className="text-neutral-400 hover:text-red-700" data-testid={`expense-delete-${expense.id}`}>
            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function ExpenseTable({ items, categories, editId, editDraft, setEditDraft, onEdit, onCancel, onSave, onDelete }) {
  return (
    <div className="border border-neutral-200 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-neutral-900 text-white">
          <tr>
            <HeaderCell>Date</HeaderCell>
            <HeaderCell>Category</HeaderCell>
            <HeaderCell>Vendor</HeaderCell>
            <HeaderCell>Notes</HeaderCell>
            <HeaderCell align="right">Amount</HeaderCell>
            <th className="w-20" />
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan={6} className="text-center text-neutral-500 py-10">No expenses logged yet.</td>
            </tr>
          ) : (
            items.map((e) =>
              editId === e.id ? (
                <EditRow
                  key={e.id}
                  id={e.id}
                  draft={editDraft}
                  setDraft={setEditDraft}
                  categories={categories}
                  onSave={onSave}
                  onCancel={onCancel}
                />
              ) : (
                <ReadRow key={e.id} expense={e} onEdit={onEdit} onDelete={onDelete} />
              ),
            )
          )}
        </tbody>
      </table>
    </div>
  );
}
